import React, { useEffect } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { IWorkProfile } from '../../types/global/helper';
import { DropdownSelect } from '../DropdownSelect/DropdownSelect';
import CustomButton from '../Button/CustomButton';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';

/** Display-only: e.g. content_creator → "Content creator". Values stay API-safe. */
function formatWorkProfileTypeLabel(value: string): string {
    if (value === 'Choose a profile') return 'Choose a Profile';
    const parts = value.split('_');
    if (parts.length === 0) return value;
    const first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
    const rest = parts
        .slice(1)
        .map(p => p.toLowerCase())
        .join(' ');
    return rest ? `${first} ${rest}` : first;
}

interface ProfileSelectorProps {
    profiles?: IWorkProfile[];
    onProfileSelect: (profile: IWorkProfile) => void;
    onEditClick?: (profile: IWorkProfile) => void;
    external?: boolean;
    isEditMode?: boolean;
    onCancelClick?: () => void;
    sx?: SxProps<Theme>;
    mainSx?: SxProps<Theme>;
    selectContainerSx?: SxProps<Theme>;
    selectDatalistStyle?: React.CSSProperties;
    hideEditButton?: boolean;
}

const WorkProfileSelector: React.FC<ProfileSelectorProps> = ({
    profiles,
    onProfileSelect,
    onEditClick,
    external,
    onCancelClick,
    isEditMode,
    sx,
    mainSx,
    selectContainerSx,
    selectDatalistStyle,
    hideEditButton = false,
}) => {
    const { themeStyle } = useThemeContext();

    const [selectedProfile, setSelectedProfile] = React.useState<IWorkProfile | undefined>();

    const handleChange = (index: number) => {
        const selectedProfile = profiles && profiles[index] && profiles[index];
        if (selectedProfile) {
            setSelectedProfile(selectedProfile);
            onProfileSelect(selectedProfile);
        }
    };

    useEffect(() => {
        if (profiles) {
            setSelectedProfile(profiles[0]);
            onProfileSelect(profiles[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box display="flex" alignItems="center" gap={1} justifyContent={'flex-end'} p={2} sx={mainSx}>
            {!external && !hideEditButton && (
                <CustomButton
                    label={isEditMode ? 'Cancel' : 'Edit'}
                    onClick={() =>
                        isEditMode && onCancelClick
                            ? onCancelClick()
                            : selectedProfile && onEditClick && onEditClick(selectedProfile)
                    }
                    sx={{
                        width: '200px',
                        borderRadius: '2px',
                        height: '60px',
                        backgroundColor: theme[themeStyle].highlight,
                        color: theme[themeStyle].black,
                        '&:hover': {
                            backgroundColor: theme[themeStyle].primary_orange,
                            WebkitTextFillColor: theme[themeStyle].black,
                        },
                    }}
                    customStyle={{
                        borderRadius: '8px',
                        height: '50px',
                    }}
                />
            )}

            {profiles && (
                <DropdownSelect
                    data={[
                        'Choose a profile',
                        ...profiles.map(p => {
                            return p.profile_type;
                        }),
                    ]}
                    formatOptionLabel={formatWorkProfileTypeLabel}
                    label={''}
                    customVariant="workProfile"
                    sx={{
                        width: {
                            xs: '100%',
                            sm: '200px',
                        },
                        ...sx,
                    }}
                    selectContainerSx={selectContainerSx}
                    selectDatalistStyle={selectDatalistStyle}
                    onOptionSelected={(_, index) => {
                        if (index > 0) handleChange(index - 1);
                    }}
                    selectProps={{
                        value: selectedProfile ? selectedProfile.profile_type : 'Choose a profile',
                        placeholder: 'Choose a profile',
                    }}
                    labelstyle={{
                        fontSize: 16,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                    }}
                />
            )}
        </Box>
    );
};

export { WorkProfileSelector };
